import * as React from 'react';
import { cn } from '@lqa-frontend/ui-system';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { cva } from 'class-variance-authority';
import { Icon } from '../icon';
import { Button } from '../buttons/Button';

type AccordionVariant = 'card' | 'inline';

type IAccordionRootProps = React.ComponentProps<
	typeof AccordionPrimitive.Root
> & {
	variant: AccordionVariant;
};

interface IAccordionTriggerProps extends React.ComponentProps<
	typeof AccordionPrimitive.Trigger
> {
	icon?: React.ReactNode;
	shouldHideArrow?: boolean;
}

const AccordionVariantContext = React.createContext<AccordionVariant>('inline');
const useAccordionVariant = () => React.useContext(AccordionVariantContext);

const rootVariants = cva('flex w-full flex-col', {
	variants: {
		variant: { card: 'gap-12', inline: '' },
	},
});

const itemVariants = cva('', {
	variants: {
		variant: {
			card: 'border border-outline-neutral rounded-m',
			inline: 'border-b border-b-divider last:border-b-0',
		},
	},
});

const triggerVariants = cva(
	'group/accordion-trigger flex flex-1 items-start justify-between gap-8 text-left text-16 font-medium text-text-primary transition-colors outline-none hover:bg-brand-blue-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-outline-interactive disabled:pointer-events-none disabled:opacity-50',
	{
		variants: {
			variant: {
				card: 'py-16 px-24 border-b border-b-transparent data-[state=open]:border-b-outline-neutral data-[state=open]:rounded-none',
				inline: 'py-16 px-12 rounded-s',
			},
		},
	}
);

const contentInnerVariants = cva('', {
	variants: {
		variant: {
			card: 'p-24',
			inline: 'px-12 pt-0 pb-16 [&_p:not(:last-child)]:mb-4',
		},
	},
});

function AccordionRoot({ variant, className, ...props }: IAccordionRootProps) {
	return (
		<AccordionVariantContext.Provider value={variant}>
			<AccordionPrimitive.Root
				data-slot="accordion"
				data-variant={variant}
				className={cn(rootVariants({ variant }), className)}
				{...props}
			/>
		</AccordionVariantContext.Provider>
	);
}

function CardAccordion(
	props: React.ComponentProps<typeof AccordionPrimitive.Root>
) {
	return <AccordionRoot variant="card" {...props} />;
}

function InlineAccordion(
	props: React.ComponentProps<typeof AccordionPrimitive.Root>
) {
	return <AccordionRoot variant="inline" {...props} />;
}

// ── Shared building blocks ──────────────────────────────────────────────────

function AccordionItem({
	className,
	...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) {
	const variant = useAccordionVariant();

	return (
		<AccordionPrimitive.Item
			data-slot="accordion-item"
			className={cn(itemVariants({ variant }), className)}
			{...props}
		/>
	);
}

function AccordionTrigger({
	className,
	children,
	icon,
	shouldHideArrow = false,
	...props
}: IAccordionTriggerProps) {
	const variant = useAccordionVariant();

	return (
		<AccordionPrimitive.Header className="flex">
			<AccordionPrimitive.Trigger
				data-slot="accordion-trigger"
				className={cn(triggerVariants({ variant }), className)}
				{...props}
			>
				<div className="flex flex-1 items-start gap-8">
					{icon && <span className="shrink-0">{icon}</span>}
					<div className="flex flex-1 flex-wrap items-center gap-x-4 gap-y-4">
						{children}
					</div>
				</div>
				{!shouldHideArrow && (
					<Icon
						name="solidArrowDown"
						size="s"
						className="text-neutral group-hover/accordion-trigger:text-text-link pointer-events-none shrink-0 self-center transition-transform duration-200 group-data-[state=open]/accordion-trigger:rotate-180"
					/>
				)}
			</AccordionPrimitive.Trigger>
		</AccordionPrimitive.Header>
	);
}

function AccordionTitle({
	className,
	children,
	...props
}: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="accordion-title"
			className={cn(
				'w-full text-16 font-semibold text-text-primary group-hover/accordion-trigger:underline',
				className
			)}
			{...props}
		>
			{children}
		</div>
	);
}

function AccordionDescription({
	className,
	children,
	...props
}: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="accordion-description"
			className={cn('text-14 text-neutral', className)}
			{...props}
		>
			{children}
		</div>
	);
}

function AccordionLink({
	className,
	onClick,
	...props
}: React.ComponentProps<'button'>) {
	return (
		<Button
			data-slot="accordion-link"
			variant="link"
			size="xs"
			className={cn('h-auto shrink-0 p-0', className)}
			onClick={(event) => {
				event.stopPropagation();
				onClick?.(event);
			}}
			{...props}
		/>
	);
}

function AccordionContent({
	className,
	children,
	...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) {
	const variant = useAccordionVariant();

	return (
		<AccordionPrimitive.Content
			data-slot="accordion-content"
			className="overflow-hidden text-14 text-text-primary data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
			{...props}
		>
			<div className={cn(contentInnerVariants({ variant }), className)}>
				{children}
			</div>
		</AccordionPrimitive.Content>
	);
}

const accordionParts = {
	Item: AccordionItem,
	Trigger: AccordionTrigger,
	Title: AccordionTitle,
	Description: AccordionDescription,
	Link: AccordionLink,
	Content: AccordionContent,
};

const CardAccordionNamespace = Object.assign(CardAccordion, accordionParts);
const InlineAccordionNamespace = Object.assign(InlineAccordion, accordionParts);

export {
	CardAccordionNamespace as CardAccordion,
	InlineAccordionNamespace as InlineAccordion,
};
